import { useRef, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import type { ContentProfile } from '@content-planner/shared';
import { extractPdfText, readTextFile } from '../../lib/pdf.js';

type FormShape = ContentProfile & { month?: string };

const ACCEPTED = '.pdf,.txt,.md,application/pdf,text/plain,text/markdown';
const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB

export function StepGreat8({ form }: { form: UseFormReturn<FormShape> }) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [parsing, setParsing] = useState(false);
  const [fileName, setFileName] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();

  const great8 = form.watch('great8') ?? '';
  const charCount = great8.trim().length;
  const businessDescription = form.watch('businessDescription') ?? '';

  async function handleFile(file: File) {
    setError(undefined);
    if (file.size > MAX_FILE_BYTES) {
      setError('That file is larger than 10 MB. Try a smaller file or paste the text below.');
      return;
    }
    setParsing(true);
    try {
      const text =
        file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
          ? await extractPdfText(file)
          : await readTextFile(file);
      const trimmed = text.trim();
      if (!trimmed) {
        setError("We couldn't read any text from that file. Try pasting it below instead.");
        return;
      }
      form.setValue('great8', trimmed, { shouldDirty: true, shouldValidate: true });
      setFileName(file.name);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'Something went wrong reading that file. Try pasting the text below.',
      );
    } finally {
      setParsing(false);
    }
  }

  function clear() {
    form.setValue('great8', '', { shouldDirty: true });
    setFileName(undefined);
    setError(undefined);
    if (fileInput.current) fileInput.current.value = '';
  }

  return (
    <div>
      <h2 className="mb-1">Have your Great 8 on hand?</h2>
      <p className="text-text-secondary mb-2">
        Upload the brand foundation document you built earlier in the Spring Challenge and we'll
        ground every post in your voice, audience, values, and the way you actually talk about
        your work.
      </p>
      <p className="text-text-secondary mb-6 text-sm">
        <strong className="text-text-primary">Optional.</strong> Skip this if you don't have one — your plan will still work, just with less of your specific brand woven in.
      </p>

      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={fileInput}
            type="file"
            accept={ACCEPTED}
            className="sr-only"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleFile(f);
            }}
          />
          <button
            type="button"
            className="gcm-btn gcm-btn-secondary"
            onClick={() => fileInput.current?.click()}
            disabled={parsing}
          >
            {parsing ? 'Reading...' : charCount > 0 ? 'Replace file' : 'Upload Great 8 (PDF, TXT, MD)'}
          </button>
          {charCount > 0 && (
            <button type="button" className="gcm-btn gcm-btn-ghost" onClick={clear} disabled={parsing}>
              Remove
            </button>
          )}
        </div>

        {fileName && charCount > 0 && (
          <p className="mt-3 text-sm text-text-secondary">
            Loaded <strong className="text-text-primary">{fileName}</strong> — {charCount.toLocaleString()} characters of brand context.
          </p>
        )}

        {error && <p className="mt-3 text-sm text-danger">{error}</p>}
      </div>

      <label className="block mb-2">
        <span className="font-heading font-semibold text-base">Or paste the text directly</span>
        <span className="block text-sm text-text-secondary mb-2">
          If the file won't upload, copy the contents of your Great 8 and paste them here.
        </span>
        <textarea
          rows={8}
          maxLength={20000}
          className="gcm-input font-body"
          placeholder="Paste the Good Work Statement, Audience, Problems & Solutions, Core Values, Brand Voice, Brand Standouts, CTAs, and Differentiators..."
          value={great8}
          onChange={(e) => form.setValue('great8', e.target.value, { shouldDirty: true })}
        />
      </label>
      <p className="text-xs text-text-secondary">
        Max 20,000 characters. Nothing is stored on a server beyond what's needed to generate this plan.
      </p>

      <div className="my-6 flex items-center gap-3" aria-hidden="true">
        <span className="h-px flex-1 bg-border-subtle" />
        <span className="gcm-eyebrow">Don't have one?</span>
        <span className="h-px flex-1 bg-border-subtle" />
      </div>

      <label className="block mb-2">
        <span className="font-heading font-semibold text-base">
          Tell us about your business in your own words
        </span>
        <span className="block text-sm text-text-secondary mb-2">
          Two or three sentences: what you do, who you help, and what makes you different. This is
          the single biggest thing that makes the plan sound like you.
        </span>
        <textarea
          rows={4}
          maxLength={500}
          className="gcm-input font-body"
          placeholder="e.g. We're a two-person bookkeeping firm for trades businesses — electricians, plumbers, landscapers. We keep their books clean and their taxes painless so they can stay on the tools, not in a spreadsheet."
          {...form.register('businessDescription', { maxLength: 500 })}
        />
      </label>
      <p className="text-xs text-text-secondary">
        {businessDescription.trim().length}/500 characters. Optional, but recommended.
      </p>
    </div>
  );
}
